import * as React from "react";

type ProSettingsProps = {
  isProPanel: boolean;
  galleryCtx: null;
};

const Setting = ({ children }) => <div className="vm-mb-4">{children}</div>;

const SettingLabel = ({ children }) => (
  <div className="vm-font-semibold vm-text-gray-700 vm-block vm-mb-1">
    {children}
  </div>
);

const ProSettings = (props: ProSettingsProps) => {
  const ctx = props.galleryCtx;

  const handleUpdate = (payload) => {
    ctx.dispatch({
      type: `EDIT_GALLERY_STATE`,
      payload,
    });
  };

  return (
    <>
      <div className="vm-p-4">
        <Setting>
          <SettingLabel>Sort videos by</SettingLabel>
          {/* Specifies how frequently Vimeography should check your Vimeo source for any new videos that may have been added. */}
          <select
            value={ctx.state.sort}
            onChange={(e) => handleUpdate({ sort: e.target.value })}
          >
            <option value="date">date added</option>
            <option value="likes">number of likes</option>
            <option value="comments">number of comments</option>
            <option value="plays">number of plays</option>
            <option value="alphabetical">alphabetically</option>
            <option value="duration">duration</option>
            <option value="default">use my sort from Vimeo</option>
          </select>
        </Setting>
        {ctx.state.sort !== "default" && (
          <Setting>
            <label className="vm-flex vm-items-start">
              <input
                type="checkbox"
                checked={ctx.state.direction === `asc`}
                className="vm-transform vm-translate-y-1"
                onChange={(e) =>
                  handleUpdate({
                    direction: e.target.checked ? `asc` : `desc`,
                  })
                }
              />
              <div>
                <SettingLabel>Reverse sort order</SettingLabel>
              </div>
            </label>
          </Setting>
        )}
        <Setting>
          <SettingLabel>Videos per page</SettingLabel>
          <input
            type="number"
            value={ctx.state.videos_per_page}
            onChange={(e) =>
              handleUpdate({ videos_per_page: parseInt(e.target.value) })
            }
            className="vm-mb-2"
          />
          <p className="vm-text-xs vm-text-gray-400">
            Sets the number of videos that should show up on each gallery page.
            Max is 100 videos per page.
          </p>
        </Setting>

        <Setting>
          <label className="vm-flex vm-items-start">
            <input
              type="checkbox"
              checked={ctx.state.enable_search}
              className="vm-transform vm-translate-y-1"
              onChange={(e) =>
                handleUpdate({ enable_search: e.target.checked })
              }
            />
            <div>
              <SettingLabel>Enable searching</SettingLabel>
              <p className="vm-text-xs vm-text-gray-400">
                When checked, allows your viewers to search this gallery for a
                specific video by its title or description.
              </p>
            </div>
          </label>
        </Setting>

        <Setting>
          <label className="vm-flex vm-items-start">
            <input
              type="checkbox"
              checked={ctx.state.enable_playlist}
              className="vm-transform vm-translate-y-1"
              onChange={(e) =>
                handleUpdate({ enable_playlist: e.target.checked })
              }
            />
            <div>
              <SettingLabel>Enable auto-advance</SettingLabel>
              <p className="vm-text-xs vm-text-gray-400">
                When checked, viewers will automatically be shown the next
                queued video in your gallery as soon as they complete a video.
              </p>
            </div>
          </label>
        </Setting>

        <Setting>
          <label className="vm-flex vm-items-start">
            <input
              type="checkbox"
              checked={ctx.state.allow_downloads}
              className="vm-transform vm-translate-y-1"
              onChange={(e) =>
                handleUpdate({ allow_downloads: e.target.checked })
              }
            />
            <div>
              <SettingLabel>Show download links</SettingLabel>
              <p className="vm-text-xs vm-text-gray-400 vm-mb-2">
                You can only allow downloads if the videos are coming from your
                own Vimeo account and you are a Vimeo Pro member.
              </p>
              <p className="vm-text-xs vm-text-gray-400">
                Download links expire after 3 hours, so make sure to set your
                video refresh settings to every hour to prevent serving expired
                download links to your viewers.
              </p>
            </div>
          </label>
        </Setting>
      </div>
    </>
  );
};

export default ProSettings;
